import { useQuery } from '@apollo/client';
import {
  EEntityOwnership,
  EFilterType,
  TEntityOwnership,
  TFilter,
  TFilterEvent,
} from 'components/Filter/filter.types';
import { useDialog, useLogEvent } from 'components/hooks';
import useSiteMutations from 'components/Sites/hooks/useSiteMutations';
import { SITES_TABLE_ROWS_PER_PAGE } from 'constants/pagination';
import { GET_SITES } from 'graphql/secure/queries/site.queries';
import { useEffect, useMemo, useState } from 'react';
import { ESiteType, IClusterSite, ISite, SiteViewOption, TSiteType } from 'types/site.types';
import { GraphQlConnection } from 'types/types';
import {
  createEnumArrayParam,
  createEnumParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

interface IUseSitesProps {
  // Can be used to skip the useQueryParam search and use the internal search term instead
  skipUseQueryParams?: boolean;

  skipCache?: boolean;
  skipQuery?: boolean;
  siteClusterId?: string;
  hasNoCluster?: boolean;
  excludeEmptyClusters?: boolean;
  clusterSitesType?: ESiteType;

  defaultOwnedById?: string;
  defaultViewOption?: SiteViewOption;
  defaultSiteTypes?: ESiteType[];
}

interface IQueryState {
  searchTerm?: string | null;
  owner?: TEntityOwnership | null;
  ownedById?: string | null;
  siteTypes?: TSiteType[] | null;
  viewOption?: SiteViewOption;
}

const useSites = ({
  skipUseQueryParams,
  skipCache,
  skipQuery,
  siteClusterId,
  hasNoCluster,
  excludeEmptyClusters,
  clusterSitesType,
  defaultOwnedById,
  defaultViewOption = SiteViewOption.CLUSTER,
  defaultSiteTypes = undefined,
}: IUseSitesProps = {}) => {
  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, null),
      owner: withDefault(
        createEnumParam<TEntityOwnership>(Object.values(EEntityOwnership)),
        defaultOwnedById ? EEntityOwnership.PARTNER : null
      ),
      ownedById: withDefault(StringParam, defaultOwnedById || null),
      siteTypes: withDefault(
        createEnumArrayParam<TSiteType>(Object.values(ESiteType)),
        defaultSiteTypes
      ),
      viewOption: withDefault(
        createEnumParam<SiteViewOption>([SiteViewOption.CLUSTER, SiteViewOption.LIST]),
        defaultViewOption || SiteViewOption.CLUSTER
      ),
    },
    { removeDefaultsFromUrl: true }
  );

  const [internalQueryState, setInternalQueryState] = useState<IQueryState>({
    searchTerm: '',
    owner: defaultOwnedById ? EEntityOwnership.PARTNER : null,
    ownedById: defaultOwnedById,
    siteTypes: defaultSiteTypes,
    viewOption: defaultViewOption,
  });

  const {
    searchTerm,
    owner: ownership,
    ownedById,
    siteTypes,
    viewOption,
  } = skipUseQueryParams ? internalQueryState : query;

  const { logEvent } = useLogEvent();
  const { openDialog } = useDialog();
  const { deleteSite } = useSiteMutations();

  const { data, loading, error, fetchMore, refetch } = useQuery<{
    sites: GraphQlConnection<ISite>;
  }>(GET_SITES, {
    variables: {
      ...(searchTerm || ownership || siteClusterId || defaultOwnedById || siteTypes || hasNoCluster
        ? {
            filters: {
              ...(searchTerm ? { searchTerm } : {}),
              ...((defaultOwnedById || ownership === EEntityOwnership.PARTNER) && {
                ownedById: defaultOwnedById || ownedById,
              }),

              ...(ownership && ownership !== EEntityOwnership.PARTNER
                ? { ownership: ownership }
                : { ownership: null }),
              ...(siteTypes ? { siteTypes } : {}),
              ...(siteClusterId ? { siteClusterId } : {}),
              ...(hasNoCluster ? { hasNoCluster } : {}),
              ...(excludeEmptyClusters ? { excludeEmptyClusters } : {}),
              ...(clusterSitesType ? { clusterSitesType } : {}),
            },
          }
        : {}),
      siteViewOption: viewOption,
      first: SITES_TABLE_ROWS_PER_PAGE,
      fetchPolicy: skipCache ? 'no-cache' : 'cache-first',
      skip: skipQuery,
    },
  });

  const sites: ISite[] = useMemo(
    () => (data?.sites ? removeGraphConnections(data.sites) : []),
    [data]
  );

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.OWNERSHIP,
        value: ownership
          ? {
              option: ownership,
              ownedById: ownedById,
            }
          : undefined,
      },
      {
        type: EFilterType.SITE_TYPE,
        value: siteTypes,
      },
    ],
    [ownership, ownedById, siteTypes]
  );

  const hasNextPage: boolean = !!data?.sites?.pageInfo?.hasNextPage;
  const endCursor: string | undefined = data?.sites?.pageInfo?.endCursor;

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  useEffect(() => {
    if (!sites?.length && hasNextPage) {
      handlePageEndReached();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, hasNextPage]);

  const handleManageClusterSites = (siteClusterId: string) => {
    const parentSiteCluster = sites.find(site => site.id === siteClusterId);
    if (parentSiteCluster?.siteType !== ESiteType.CLUSTER) {
      throw new Error('This dialog can only be used for cluster sites');
    }
    openDialog({
      type: 'ADD_MANAGE_CLUSTER_DECISION',
      props: { siteCluster: parentSiteCluster, partner: parentSiteCluster?.ownedBy },
    });
  };

  const handleCreateSite = ({
    siteClusterId,
    defaultSiteType,
    defaultRawMaterialId,
    defaultCountry,
  }: {
    siteClusterId?: string;
    defaultSiteType?: ESiteType;
    defaultRawMaterialId?: string;
    defaultCountry?: string;
  } = {}) => {
    openDialog({
      type: 'ADD_EDIT_SITE',
      // In the client view internal sites we prefill the company with your own company
      props: {
        siteClusterId,
        defaultSiteType,
        defaultRawMaterialId,
        defaultCountry,
      },
    });
  };

  const handleEditSite = (site: ISite) => {
    openDialog({
      type: 'ADD_EDIT_SITE',
      props: {
        site,
      },
    });
  };

  const handleCreateSiteCluster = () => {
    openDialog({
      type: 'ADD_EDIT_SITE_CLUSTER',
    });
  };

  const handleEditSiteCluster = (siteCluster: IClusterSite) => {
    openDialog({
      type: 'ADD_EDIT_SITE_CLUSTER',
      props: {
        siteCluster,
      },
    });
  };

  const handleDeleteSite = (siteId: string) => {
    const siteTitle = sites.find(site => site.id === siteId)?.title;
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete site',
        text: 'Are you sure you want to delete this site? All data will be lost and you will not be able to recover this item.',
        submitText: 'Delete',
        itemTitle: siteTitle,
        displayCloseButton: true,
        onSubmit: () => {
          deleteSite({ variables: { id: siteId } });
        },
        onCancel: () => undefined,
      },
    });
  };

  const handleDeleteSiteCluster = (siteId: string) => {
    const site = sites.find(site => site.id === siteId);
    if (site?.siteType !== ESiteType.CLUSTER) {
      throw new Error('This dialog can only be used for cluster');
    }
    openDialog({
      type: 'DELETE_SITE_CLUSTER',
      props: {
        siteCluster: site,
      },
    });
  };

  const handleEditFarmPlots = (site: ISite) => {
    if (site.siteType !== ESiteType.FARM) {
      throw new Error('This dialog can only be used for farms');
    }
    openDialog({
      type: 'ADD_EDIT_PLOTS',
      props: {
        // The plots of the farm were not loaded in the site query
        farm: site,
      },
    });
  };

  const handleManageSite = (site: ISite) => {
    if (site.siteType === ESiteType.CLUSTER) {
      throw new Error('This dialog can not be used for clusters');
    }
    openDialog({
      type: 'MANAGE_SITE',
      props: {
        site,
      },
    });
  };

  const handleSearchTermChange = (searchTerm: string) => {
    logEvent('SITE_SEARCHED', { searchTerm });
    skipUseQueryParams
      ? setInternalQueryState(prev => ({ ...prev, searchTerm }))
      : setQuery(prev => ({ ...prev, searchTerm }));
  };

  const handleSiteViewOptionChange = (viewOption: SiteViewOption) => {
    skipUseQueryParams
      ? setInternalQueryState(prev => ({ ...prev, viewOption }))
      : setQuery(prev => ({ ...prev, viewOption }));
  };

  const handleFilterChange = (event: TFilterEvent) => {
    if (event.type === EFilterType.OWNERSHIP) {
      skipUseQueryParams
        ? setInternalQueryState(prev => ({
            ...prev,
            owner: event.value?.option || undefined,
            ownedById: event.value?.ownedById || undefined,
          }))
        : setQuery(prev => ({
            ...prev,
            owner: event.value?.option || undefined,
            ownedById: event.value?.ownedById || undefined,
          }));
    } else if (event.type === EFilterType.SITE_TYPE) {
      skipUseQueryParams
        ? setInternalQueryState(prev => ({
            ...prev,
            siteTypes: event.value || undefined,
          }))
        : setQuery(prev => ({
            ...prev,
            siteTypes: event.value || undefined,
          }));
    }
  };

  const handleFilterReset = () => {
    skipUseQueryParams
      ? setInternalQueryState(prev => ({
          ...prev,
          owner: undefined,
          ownedById: undefined,
          siteTypes: undefined,
        }))
      : setQuery(prev => ({
          owner: undefined,
          ownedById: undefined,
          siteTypes: undefined,
        }));
  };

  return {
    sites,
    loading,
    error,
    searchTerm,
    hasNextPage,
    filters,
    viewOption,
    refetch,
    handleSiteViewOptionChange,
    handleSearchTermChange,
    handleCreateSite,
    handleEditSite,
    handleDeleteSite,
    handleCreateSiteCluster,
    handleEditSiteCluster,
    handlePageEndReached,
    handleDeleteSiteCluster,
    handleManageClusterSites,
    handleFilterChange,
    handleFilterReset,
    handleEditFarmPlots,
    handleManageSite,
  };
};

export default useSites;
